import { useMemo, useState } from 'react'
import type { ColumnDef } from '@tanstack/react-table'
import { Plus } from 'lucide-react'
import { DataTable } from '@/components/data-table'
import { Notice, PageHeader } from '@/components/page'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { QuestEditor } from '@/features/quests/quest-editor'
import {
  LIVE_BADGE,
  LIVE_LABEL,
  questLiveState,
  useQuestList,
  type QuestRow,
} from '@/features/quests/api'
import { localStamp } from '@/lib/datetime'

const PAGE_SIZE = 20

/** The window as one cell — two dates in two columns read as unrelated. */
function windowCell(quest: QuestRow) {
  if (!quest.starts_at && !quest.ends_at) return 'Always on'
  const from = quest.starts_at ? localStamp(quest.starts_at) : 'On publish'
  const to = quest.ends_at ? localStamp(quest.ends_at) : 'No expiry'
  return `${from} → ${to}`
}

export function QuestsPage() {
  const [page, setPage] = useState(1)
  /** `{}` opens the editor for a new quest; `{ id }` for an existing one. */
  const [editing, setEditing] = useState<{ id?: number } | null>(null)

  const query = useQuestList({ page, pageSize: PAGE_SIZE })

  const columns = useMemo<ColumnDef<QuestRow, unknown>[]>(
    () => [
      {
        accessorKey: 'id',
        header: 'ID',
        // Sorting stays off: the list is server-paginated and data-table sorts
        // client-side, so a sort header would only order the loaded page.
        enableSorting: false,
        cell: ({ row }) => (
          <code className="rounded bg-muted px-1.5 py-0.5 text-xs text-muted-foreground">
            {row.original.id}
          </code>
        ),
      },
      {
        accessorKey: 'name',
        header: 'Quest',
        cell: ({ row }) => (
          <div>
            <div className="font-semibold">{row.original.name}</div>
            <div className="text-xs text-faint">{windowCell(row.original)}</div>
          </div>
        ),
      },
      {
        id: 'live',
        header: 'State',
        enableSorting: false,
        cell: ({ row }) => {
          // Derived on read, never stored — see questLiveState. A quest can be
          // `active` and still show as Scheduled or Ended.
          const state = questLiveState(row.original)
          return <Badge variant={LIVE_BADGE[state]}>{LIVE_LABEL[state]}</Badge>
        },
      },
      {
        id: 'rule',
        header: 'Completion',
        enableSorting: false,
        cell: ({ row }) => {
          const quest = row.original
          return (
            <span className="text-sm">
              {quest.completion_rule === 'ALL'
                ? `All ${quest.sub_task_count}`
                : `${quest.threshold ?? '?'} of ${quest.sub_task_count}`}
              <span className="text-faint">
                {' '}
                sub-task{quest.sub_task_count === 1 ? '' : 's'}
              </span>
            </span>
          )
        },
      },
      {
        accessorKey: 'reward_points',
        header: 'Reward',
        cell: ({ row }) => (
          <span className="text-sm">
            <b>{row.original.reward_points}</b>
            <span className="text-faint"> pts · </span>
            {row.original.reward_release === 'require_approval' ? 'on approval' : 'auto'}
          </span>
        ),
      },
      {
        id: 'actions',
        header: '',
        enableSorting: false,
        cell: ({ row }) => (
          <div className="flex justify-end whitespace-nowrap">
            {/* One action either way. A published quest opens the same editor
                read-only — publishing is deliberately only reachable from inside
                the editor, so nobody makes a quest live from a list row without
                having just looked at what it awards. */}
            <Button variant="secondary" size="sm" onClick={() => setEditing({ id: row.original.id })}>
              {row.original.status === 'draft' ? 'Edit draft' : 'View'}
            </Button>
          </div>
        ),
      },
    ],
    [],
  )

  return (
    <>
      <PageHeader
        title="Quests"
        actions={
          <Button onClick={() => setEditing({})}>
            <Plus />
            New quest
          </Button>
        }
      />

      <Notice>
        <b>A published quest is immutable</b> — only its end date can change afterwards. Fix a
        mistake by ending the quest and publishing a replacement, never by editing it under members
        who are part-way through. Every member is enrolled automatically when a quest goes live, and{' '}
        <b>whether a quest is live is worked out on every read</b> from its window — nothing flips a
        flag on a schedule.
      </Notice>

      <Card>
        <DataTable
          columns={columns}
          query={query}
          pagination={{ page, onPageChange: setPage }}
          emptyMessage="No quests yet."
        />
      </Card>

      {/* Keyed so switching rows refetches and rebuilds the form from that
          quest's own tasks rather than reusing the previous one's. */}
      {editing && (
        <QuestEditor
          key={editing.id ?? 'new'}
          id={editing.id}
          open
          onOpenChange={(open) => !open && setEditing(null)}
        />
      )}
    </>
  )
}
